import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { find, capitalize, isEqual } from 'lodash';
import isEmail from 'validator/lib/isEmail';
import { FormSections, useLocalizeMessage, ModalWindow, Spinner, useToastNotification, } from 'libs.nucleus';
import { DisplayNameField } from './form_items/display_name.component';
import { EmailField } from './form_items/email.component';
import { createAndInviteCaregiver, createCaregiver, getFormInitialValues, updateCaregiverEmail, } from './helpers/caregiver_form.helpers';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { CaregiverStatus, getCaregiverStatus } from '../../helpers/caregivers.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useForm } from '../../hooks/use_form';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
export const CaregiverFormModal = ({ isModalOpen, caregiver, onClose, participant, onView, onDisable, onEnable, dataTestId, }) => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const { isUserPermitted } = useLumosAuth();
    const initialValues = getFormInitialValues({
        caregiver,
        totalCaregivers: participant.c_caregiver_relationship?.c_caregivers_info?.length || 0,
    });
    const { form, isFieldtouched, onFieldChange } = useForm({ initialValues });
    const { addNotification } = useToastNotification();
    const queryClient = useQueryClient();
    const handleEnable = (caregiver) => {
        onEnable(caregiver, onClose);
    };
    const handleDisable = (caregiver) => {
        onDisable(caregiver, onClose);
    };
    const addCaregiverMutation = useMutation({
        mutationFn: createCaregiver,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['participantDetails', participant._id] });
            addNotification({
                title: '',
                subtitle: translate('info.caregiver.record_created'),
                type: 'success',
            });
            onClose();
        },
        onError: (e) => {
            const message = getApiErrorMessage(e);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        },
    });
    const addAndInviteCaregiverMutation = useMutation({
        mutationFn: createAndInviteCaregiver,
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({ queryKey: ['participantDetails', participant._id] });
            addNotification({
                title: '',
                subtitle: translate('info.caregiver.record_created'),
                type: 'success',
            });
            const _caregiverInfo = find(data.caregiverRelationship.c_caregivers_info, ({ c_public_user: { _id } }) => _id === data.caregiverUser._id);
            const caregiverInfo = {
                ..._caregiverInfo,
                c_public_user: data.caregiverUser,
            };
            onView(caregiverInfo);
            onClose();
        },
        onError: (e) => {
            const message = getApiErrorMessage(e);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        },
    });
    const updateCaregiverMutation = useMutation({
        mutationFn: updateCaregiverEmail,
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['participantDetails', participant._id] });
            addNotification({
                title: '',
                subtitle: translate('info.caregiver.record_updated'),
                type: 'success',
            });
            onClose();
        },
        onError: (e) => {
            const message = getApiErrorMessage(e);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        },
    });
    const errors = {
        email: form?.email && !isEmail(form?.email) ? translate('validation.invalid_email') : undefined,
        displayName: !form?.displayName ? translate('validation.caregiver.display_name_required') : undefined,
    };
    const formHasErrors = Object.values(errors).some((error) => !!error);
    const submitForm = (event) => {
        event.preventDefault();
        if (formHasErrors) {
            return;
        }
        if (!environment) {
            throw new Error('Environment not defined');
        }
        if (caregiver) {
            updateCaregiverMutation.mutate({
                environment,
                form,
                participantId: participant._id,
                caregiverId: caregiver.c_public_user._id,
            });
        }
        else {
            addAndInviteCaregiverMutation.mutate({ environment, form, participantId: participant._id });
        }
    };
    const formSections = [
        {
            title: translate('generic.details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(DisplayNameField, { form: form, errors: errors, isFieldtouched: isFieldtouched, onFieldChange: onFieldChange }), _jsx(EmailField, { form: form, errors: errors, onFieldChange: onFieldChange, isFieldtouched: isFieldtouched })] })),
        },
    ];
    const isMutationInProgress = addCaregiverMutation.isPending || addAndInviteCaregiverMutation.isPending || updateCaregiverMutation.isPending;
    const savingButton = (_jsx("div", { className: 'flex items-center gap-3', children: _jsx(Spinner, { size: 'md' }) }));
    const title = caregiver
        ? capitalize(`${translate('generic.edit')} ${translate('generic.caregivers', { count: 1 })}`)
        : translate('action.caregiver.add');
    const noFieldsUpdated = caregiver && isEqual(initialValues, form);
    const status = caregiver
        ? getCaregiverStatus(caregiver, participant.c_caregiver_relationship?.c_caregiver_assignments)
        : null;
    const canEnableCaregiver = caregiver &&
        caregiver.c_public_user.c_access_code &&
        status === CaregiverStatus.DISABLED &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_ENABLE_CAREGIVERS);
    const canDisableCaregiver = caregiver &&
        caregiver.c_public_user.c_access_code &&
        status === CaregiverStatus.ENABLED &&
        isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_DISABLE_CAREGIVERS);
    const footerSecondaryActionButtons = [
        ...(canEnableCaregiver
            ? [
                {
                    label: translate('generic.enable'),
                    onClick: () => handleEnable(caregiver),
                    disabled: isMutationInProgress,
                    dataTestId: 'edit-caregiver-enable',
                },
            ]
            : []),
        ...(canDisableCaregiver
            ? [
                {
                    label: translate('generic.disable'),
                    onClick: () => handleDisable(caregiver),
                    disabled: isMutationInProgress,
                    dataTestId: 'edit-caregiver-disable',
                },
            ]
            : []),
        {
            label: translate('generic.cancel'),
            onClick: onClose,
            disabled: isMutationInProgress,
        },
    ];
    return (_jsx(ModalWindow, { title: title, subtitle: participant.c_number && `${translate('label.caregiver.modal_subtitle', { number: participant.c_number })}`, isOpen: isModalOpen, closeWindow: onClose, footerSecondaryActionButtons: footerSecondaryActionButtons, footerPrimaryActionButton: {
            disabled: formHasErrors || isMutationInProgress || noFieldsUpdated,
            label: caregiver ? translate('generic.save') : translate('generic.create', { tense: 'present' }),
            content: isMutationInProgress ? savingButton : undefined,
            onClick: () => {
                if (!environment) {
                    throw new Error('Environment not defined');
                }
                const event = new Event('submit');
                submitForm(event);
            },
            id: caregiver ? 'edit-caregiver-save' : 'add-caregiver-invite',
            dataTestId: caregiver ? 'edit-caregiver-save' : 'add-caregiver-invite',
        }, width: 'full', dataTestId: dataTestId, children: _jsx("form", { autoComplete: 'off', onSubmit: submitForm, className: 'mt-10 mb-24', children: _jsx(FormSections, { sections: formSections }) }) }));
};
